import React, { useState } from 'react';
import { ExpandableCard } from '@/components/shared/ExpandableCard';

interface VolunteerPathContentProps {
    onGetStarted: () => void;
    className?: string;
}

export const VolunteerPathContent: React.FC<VolunteerPathContentProps> = ({
    onGetStarted,
    className = ''
}) => {
    // State to track which volunteer cards are expanded
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

    // Function to toggle expansion of a volunteer card
    const toggleCardExpansion = (cardId: string) => {
        const newExpanded = new Set(expandedCards);
        if (newExpanded.has(cardId)) {
            newExpanded.delete(cardId);
        } else {
            newExpanded.add(cardId);
        }
        setExpandedCards(newExpanded);
    };

    const impactStats = [
        {
            label: 'Weekly Impact',
            description: 'Flexible commitment on your schedule',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            )
        },
        {
            label: 'Loneliness Reduction',
            description: 'Improve senior mental health & wellbeing',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            )
        },
        {
            label: 'Volunteer Satisfaction',
            description: 'Feel fulfilled making connections',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            )
        }
    ];

    const supportFeatures = [
        {
            title: 'Comprehensive Training',
            description: 'Free online training covering safety, communication, and best practices, plus professional business coverage and ongoing support',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
            )
        },
        {
            title: 'Background Check Coverage',
            description: 'We cover the cost of background checks and provide ongoing support',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
            )
        },
        {
            title: 'Support Hotline',
            description: 'Have a question or concern while volunteering? Our dedicated support team is always just a phone call away.',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
            )
        },
        {
            title: 'Community Building',
            description: 'Regular volunteer meetups, recognition events, and a supportive community',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            )
        }
    ];

    const volunteerExperiences = [
        {
            id: 'relationships',
            title: 'Build Relationships',
            description: 'Regular visits with the same seniors, sharing stories and creating lasting friendships that enrich both lives',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            )
        },
        {
            id: 'practical',
            title: 'Provide Practical Help',
            description: 'Assist with errands and light tasks that make daily life easier and help maintain independence',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            )
        },
        {
            id: 'interests',
            title: 'Share Your Interests',
            description: 'Use your hobbies and skills to enrich someone\'s day through meaningful shared activities',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
            )
        }
    ];

    return (
        <div className={className}>
            {/* Ways You Can Help as a Volunteer Section */}
            <section className="pt-16 pb-8 bg-green-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Ways You Can Help as a Volunteer
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Choose meaningful activities that match your interests and availability. Your time and caring spirit can make a profound difference in a senior's life. Here's how you can help
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto items-start">
                        {volunteerExperiences.map((experience) => {
                            // Define examples for each category
                            const categoryExamples = {
                                relationships: [
                                    'Friendly conversation and active listening',
                                    'Reading together or sharing stories',
                                    'Playing cards, puzzles, or board games',
                                    'Accompanying to social events'
                                ],
                                practical: [
                                    'Transportation to non-urgent appointments and errands',
                                    'Grocery shopping and errands',
                                    'Light housekeeping and organizing',
                                    'Technology support and tutoring'
                                ],
                                interests: [
                                    'Creative Arts: Painting, crafts, music, photography',
                                    'Outdoor Activities: Gardening, bird watching, walking',
                                    'Learning & Games: Book discussions, puzzles, teaching skills',
                                    'Teaching Skills: Share your expertise in any area you enjoy'
                                ]
                            };

                            const examples = categoryExamples[experience.id as keyof typeof categoryExamples] || [];

                            return (
                                <ExpandableCard
                                    key={experience.id}
                                    title={experience.title}
                                    description={experience.description}
                                    icon={experience.icon}
                                    examples={examples}
                                    colorScheme="green"
                                    isExpanded={expandedCards.has(experience.id)}
                                    onToggle={() => toggleCardExpansion(experience.id)}
                                />
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Make a Real Difference - Impact Banner */}
            <section className="pt-0 pb-0 bg-gradient-to-br from-green-600 to-green-700 text-white relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-green-600 opacity-10">
                    <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 400 400" fill="none">
                        <defs>
                            <pattern id="volunteer-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                                <circle cx="20" cy="20" r="1.5" fill="currentColor" opacity="0.3" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#volunteer-pattern)" />
                    </svg>
                </div>

                <div className="container mx-auto px-4 relative">
                    {/* Header */}
                    <div className="text-center mb-12 pt-16">
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">
                            Make a Real Difference
                        </h2>
                        <p className="text-xl text-green-100 max-w-2xl mx-auto">
                            Your time and care can transform a senior's life while enriching your own
                        </p>
                    </div>

                    {/* Impact Statistics Banner */}
                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto pb-16">
                        {impactStats.map((stat, index) => (
                            <div
                                key={index}
                                className="text-center group hover:scale-105 transition-transform duration-300"
                            >
                                {/* Icon */}
                                <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-opacity-30 transition-colors duration-300">
                                    <div className="text-white">
                                        {stat.icon}
                                    </div>
                                </div>

                                {/* Label */}
                                <div className="text-2xl font-bold text-white mb-3">
                                    {stat.label}
                                </div>

                                {/* Description */}
                                <p className="text-green-200 leading-relaxed">
                                    {stat.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* We Support Our Volunteers Section */}
            <section className="pt-0 pb-8 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12 pt-8">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            We Support Our Volunteers
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            You'll never be on your own. We provide everything you need to volunteer with confidence
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                        {supportFeatures.map((feature, index) => (
                            <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-bibi-blue rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        <div className="text-white">
                                            {feature.icon}
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                                        <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Bottom Line Summary */}
                    <div className="mt-8 bg-green-50 rounded-lg p-6 border border-green-200 max-w-4xl mx-auto">
                        <div className="text-center text-lg font-medium text-gray-900">
                            <strong>The Bottom Line:</strong> We handle the logistics, safety, and coordination so you can focus purely on the rewarding part - helping your neighbors.
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-12 bg-green-600 text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                        Ready to Help Your Neighbors?
                    </h2>
                    <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
                        Join our volunteer network and start making a difference in seniors' lives today
                    </p>
                    <button
                        onClick={onGetStarted}
                        className="bg-white text-green-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors focus:outline-none focus:ring-4 focus:ring-white/50"
                    >
                        Join Early Access List
                    </button>
                    <p className="text-sm text-green-100 mt-4">
                        Free training provided • Flexible scheduling • Launch early 2026
                    </p>
                </div>
            </section>
        </div>
    );
};